import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { ChevronDown, ChevronRight, Folder, Image as ImageIcon, ListPlus, X } from 'lucide-react';
import { Button, Checkbox, Tooltip, TooltipContent, TooltipTrigger } from '@scio3/ui';
import type { Asset, AssetGroup } from '@scio3/shared-types';
import { cn } from '@/lib/utils';
import { ScrollContainer } from '@/components/shared/ScrollContainer';
import { useScrollViewport } from '@/hooks/useScrollViewport';
import { useIntersectionLoadMore } from '@/hooks/useIntersectionLoadMore';
import { useScrollLoadMore } from '@/hooks/useScrollLoadMore';
import { AssetPickerItem, AssetFolderItem } from './AssetPickerItem';

interface AssetPickerListProps {
  assets: Asset[];
  folders: AssetGroup[];
  loading: boolean;
  /** Total asset count from API (includes items not yet loaded) */
  totalCount?: number;
  /** Total folder count from API (includes folders not yet loaded) */
  folderTotalCount?: number;
  hasNextPage: boolean;
  hasFolderNextPage: boolean;
  isFolderLoadingMore: boolean;
  selectedAssetIds: Set<string>;
  activePlaylistId: string | null;
  currentPath: string;
  onToggleAsset: (id: string) => void;
  onAddSingle: (asset: Asset) => void;
  onOpenAsset: (asset: Asset) => void;
  onAddSelected: () => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onLoadMore: () => void;
  onLoadMoreFolders: () => void;
  onNavigateFolder: (path: string, teamId?: string) => void;
  onAddFolder: (folderPath: string, folderName: string) => void;
  /** Whether the user can edit the playlist (folder permission check). */
  canEditPlaylist?: boolean;
}

const ITEM_SIZE = 48;
const OVERSCAN = 5;
const LOAD_MORE_THRESHOLD = 100;

export function AssetPickerList({
  assets,
  folders,
  loading,
  totalCount,
  folderTotalCount,
  hasNextPage,
  hasFolderNextPage,
  isFolderLoadingMore,
  selectedAssetIds,
  activePlaylistId,
  currentPath,
  onToggleAsset,
  onAddSingle,
  onOpenAsset,
  onAddSelected,
  onSelectAll,
  onClearSelection,
  onLoadMore,
  onLoadMoreFolders,
  onNavigateFolder,
  onAddFolder,
  canEditPlaylist = true,
}: AssetPickerListProps) {
  const { t } = useTranslation();
  const hasSelection = selectedAssetIds.size > 0;
  const allSelected = assets.length > 0 && selectedAssetIds.size === assets.length;
  const someSelected = hasSelection && !allSelected;

  const [foldersOpen, setFoldersOpen] = useState(true);
  const [assetsOpen, setAssetsOpen] = useState(true);

  // OverlayScrollbars scroll container + asset section ref for scrollMargin measurement
  const osRef = useRef<OverlayScrollbarsComponentRef>(null);
  const { getViewport, scrollTo } = useScrollViewport(osRef);

  // Re-expand both sections and scroll to top when navigating into a new folder.
  // Uses useLayoutEffect so the scroll reset happens before the browser paints,
  // plus rAF + setTimeout fallbacks for OverlayScrollbars which may adjust
  // scroll after commit (especially after load-more expanded the content).
  useLayoutEffect(() => {
    setFoldersOpen(true);
    setAssetsOpen(true);
    scrollTo({ top: 0 });
    const rafId = requestAnimationFrame(() => scrollTo({ top: 0 }));
    const timerId = setTimeout(() => scrollTo({ top: 0 }), 50);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
    };
  }, [currentPath, scrollTo]);
  const assetsContainerRef = useRef<HTMLDivElement>(null);
  const [assetsScrollMargin, setAssetsScrollMargin] = useState(0);

  // ── Folder virtualizer ──────────────────────────────────────────────
  const folderVirtualizer = useVirtualizer({
    count: foldersOpen ? folders.length : 0,
    getScrollElement: getViewport,
    estimateSize: () => ITEM_SIZE,
    overscan: OVERSCAN,
    getItemKey: (index) => folders[index]?._id ?? index,
  });

  // ── Asset virtualizer ───────────────────────────────────────────────
  const assetVirtualizer = useVirtualizer({
    count: assetsOpen ? assets.length : 0,
    getScrollElement: getViewport,
    estimateSize: () => ITEM_SIZE,
    overscan: OVERSCAN,
    scrollMargin: assetsScrollMargin,
    getItemKey: (index) => assets[index]?._id ?? index,
  });

  // Recalculate scrollMargin when content above the asset container changes
  const folderTotalSize = folderVirtualizer.getTotalSize();
  useLayoutEffect(() => {
    if (assetsContainerRef.current) {
      setAssetsScrollMargin(assetsContainerRef.current.offsetTop);
    }
  }, [foldersOpen, folders.length, assetsOpen, assets.length, folderTotalSize]);

  // Folder load-more via IntersectionObserver
  const folderSentinelRef = useIntersectionLoadMore({
    onLoadMore: onLoadMoreFolders,
    enabled: foldersOpen && hasFolderNextPage,
    isLoading: isFolderLoadingMore,
    getScrollRoot: getViewport,
  });

  // Scroll-based lazy loading (assets only)
  useScrollLoadMore({
    onLoadMore,
    hasNextPage,
    isLoading: loading && assets.length > 0,
    isOpen: assetsOpen,
    getScrollElement: getViewport,
    threshold: LOAD_MORE_THRESHOLD,
  });

  const isInitialLoading = loading && assets.length === 0 && folders.length === 0;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Selection action bar */}
      {hasSelection && (
        <div className="mb-2 mr-5 flex items-center gap-2 rounded-lg bg-muted/80 px-2 py-1.5 shadow-sm">
          <Checkbox
            checked={allSelected || (someSelected ? 'indeterminate' : false)}
            onCheckedChange={(checked) => {
              if (checked) {
                onSelectAll();
              } else {
                onClearSelection();
              }
            }}
            aria-label={t('Select all assets')}
          />
          <span className="text-xs font-medium text-muted-foreground">
            {selectedAssetIds.size} {t('selected')}
          </span>
          <div className="ml-auto flex items-center gap-1">
            {canEditPlaylist && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="h-6 w-6 transition-colors hover:bg-input"
                    onClick={onAddSelected}
                    aria-label={t('Add to Playlist')}
                  >
                    <ListPlus className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('Add to Playlist')}</TooltipContent>
              </Tooltip>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              className="h-6 w-6 shrink-0 transition-colors hover:bg-input"
              onClick={onClearSelection}
              aria-label={t('Clear selection')}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* ScrollContainer stays mounted across path changes so scrollTo(0) works reliably */}
      <ScrollContainer ref={osRef} className="relative pr-6">
        {isInitialLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="h-8 w-10 rounded-md bg-muted animate-pulse" />
                <div className="h-4 flex-1 rounded-sm bg-muted animate-pulse" />
              </div>
            ))}
          </div>
        ) : (
          <div className="pt-1 pb-4">
            {/* ── FOLDERS section ─────────────────────────────────────── */}
            <div className="sticky top-0 z-20 bg-card pb-1">
              <Button
                variant="ghost"
                className="flex h-8 w-full items-center justify-start gap-1 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                onClick={() => setFoldersOpen((prev) => !prev)}
              >
                {foldersOpen ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                <Folder className="h-3.5 w-3.5" />
                {t('Folders')} ({folderTotalCount ?? folders.length})
              </Button>
            </div>

            {/* Folder items (virtualized) */}
            {foldersOpen && folders.length > 0 && (
              <div
                style={{
                  height: folderVirtualizer.getTotalSize(),
                  width: '100%',
                  position: 'relative',
                }}
              >
                {folderVirtualizer.getVirtualItems().map((virtualRow) => {
                  const folder = folders[virtualRow.index];
                  return (
                    <div
                      key={`folder-${folder._id}`}
                      data-index={virtualRow.index}
                      ref={folderVirtualizer.measureElement}
                      className="pb-1"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <AssetFolderItem
                        folder={folder}
                        onNavigate={() =>
                          onNavigateFolder(
                            folder.path
                              ? `${folder.path}/${folder.name || folder._id}`
                              : `/${folder.name || folder._id}`,
                            folder.teamId,
                          )
                        }
                        onAddFolder={() => {
                          const name = folder.name || folder._id;
                          onAddFolder(folder.path ? `${folder.path}/${name}` : `/${name}`, name);
                        }}
                        canEditPlaylist={canEditPlaylist}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Folder load-more sentinel (IntersectionObserver) */}
            {foldersOpen && hasFolderNextPage && (
              <>
                <div ref={folderSentinelRef} className="h-px" />
                {isFolderLoadingMore && (
                  <div className="flex items-center justify-center py-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                )}
              </>
            )}

            {/* ── ITEMS section ───────────────────────────────────────── */}
            <div className="sticky top-9 z-10 bg-card pb-2">
              <div className="group/assets flex h-8 items-center rounded-md px-2 transition-colors hover:bg-muted">
                <Button
                  variant="ghost"
                  type="button"
                  className="h-auto flex-1 justify-start gap-1 rounded-none p-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-transparent"
                  onClick={() => setAssetsOpen((prev) => !prev)}
                >
                  {assetsOpen ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  <ImageIcon className="h-3.5 w-3.5" />
                  {t('Assets')} ({totalCount ?? assets.length})
                </Button>
              </div>
            </div>

            {/* Asset items (virtualized) */}
            {assetsOpen && assets.length > 0 && (
              <div
                ref={assetsContainerRef}
                style={{
                  height: assetVirtualizer.getTotalSize(),
                  width: '100%',
                  position: 'relative',
                }}
              >
                {assetVirtualizer.getVirtualItems().map((virtualRow) => {
                  const idx = virtualRow.index;
                  const asset = assets[idx];
                  const isSel = selectedAssetIds.has(asset._id);
                  const prevSel = idx > 0 && selectedAssetIds.has(assets[idx - 1]._id);
                  const nextSel =
                    idx < assets.length - 1 && selectedAssetIds.has(assets[idx + 1]._id);
                  return (
                    <div
                      key={`asset-${asset._id}`}
                      data-index={idx}
                      ref={assetVirtualizer.measureElement}
                      className={cn(
                        'pb-1',
                        isSel &&
                          nextSel &&
                          'after:absolute after:bottom-0 after:inset-x-0 after:h-1 after:bg-primary/10',
                      )}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start - assetsScrollMargin}px)`,
                      }}
                    >
                      <AssetPickerItem
                        asset={asset}
                        isSelected={isSel}
                        selectedAssetIds={selectedAssetIds}
                        isSelfPlaylist={asset._id === activePlaylistId}
                        onToggleSelect={() => onToggleAsset(asset._id)}
                        onAddSingle={() => onAddSingle(asset)}
                        onOpenAsset={() => onOpenAsset(asset)}
                        prevSelected={prevSel}
                        nextSelected={nextSel}
                        canEditPlaylist={canEditPlaylist}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Loading more indicator */}
            {loading && assets.length > 0 && (
              <div className="flex items-center justify-center py-3">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}
          </div>
        )}
      </ScrollContainer>
    </div>
  );
}
